<template>
  <a
    class='storage'
    :href='url'
    :data-id='isDevelopment ? id : null'
    @click='storageClick'
  >
    <div class='inner'>
      <div class='name-wrapper'>
        <div class='name'>{{ name }}</div>
      </div>
      <div v-if='discount' class='discount' v-html='discount' />
      <div
        v-for='(metro, index) in metros'
        :key='`metro-${index}`'
        class='metro'
      >
        <div class='station'>{{ metro.displayName }}</div>
        <div class='line' :style='{ background: metro.color }' />
      </div>
      <div class='price-wrapper'>
        <span
          class='badge'
          :class='{
            "is-soon": isSoon,
            "is-nearly-full": isNearlyFull
          }'
        >
          <span v-if='isSoon'>
            {{ tp('is_soon') }}
          </span>
          <template v-else>
            {{ tp('available_boxes_count', { count: availableBoxesCount }) }}
          </template>
        </span>
        <span v-if='minPrice' class='price'>
          {{ minPrice }}{{ tp('month') }}
        </span>
      </div>
      <ul class='features'>
        <li
          v-for='(tag, index) in balloonTags'
          :key='`tag-${index}`'
          v-html='tag'
        />
      </ul>
    </div>
    <div
      class='photo'
      :class='{
        "is-missing": !photoUrl,
        "is-no-boxes": !availableBoxesCount && photoUrl
      }'
      :style='{ backgroundImage: photoUrl ? `url(${photoUrl})` : undefined}'
    />
  </a>
</template>

<script>
export default {
  name: 'Storages',
  inheritAttrs: false
};
</script>

<script setup>
import inNewTab from '@/helpers/in_new_tab';
import { isMobile } from '@/helpers/mobile_detect';

import { t, prepared } from '@/helpers/i18n';

const props = defineProps({
  id: { type: Number, required: true },
  name: { type: String, required: true },
  url: { type: String, required: true },
  discount: { type: String, required: false, default: undefined },
  metros: { type: Array, required: true },
  availableBoxesCount: { type: Number, required: true },
  minPrice: { type: String, required: false, default: undefined },
  balloonTags: { type: Array, required: true },
  photoUrl: { type: String, required: false, default: undefined },
  isSoon: { type: Boolean, required: true }
});
const emit = defineEmits(['storage:click']);
const tp = prepared('frontend.storages_map-v4.storage');

const isDevelopment = window.ENV === 'development';
const isNearlyFull = props.availableBoxesCount && props.availableBoxesCount <= 5;

function storageClick(event) {
  if (inNewTab(event)) { return; }
  if (isMobile()) { return; }

  event.preventDefault();
  emit('storage:click');
}
</script>

<style scoped lang='sass'>
@import 'app/packs/stylesheets/functions/rem'
@import 'app/packs/stylesheets/mixins/responsive'
@import 'app/packs/stylesheets/mixins/typography'
@import 'app/packs/stylesheets/mixins/fonts'

.storage
  border-top: 1px solid var(--gray-300)
  cursor: pointer
  display: flex
  justify-content: space-between

  +lte_ipad
    gap: rem4(16px)
    padding: rem4(16px) 0

  +gte_laptop
    gap: 16px
    padding: 16px 0

    &:hover
      color: var(--gray-900)
      text-decoration: none

      .name
        color: var(--primary-color)

  &:active
    color: var(--gray-900)
    text-decoration: none

    .name
      color: var(--primary-color-active)

.inner
  display: flex
  flex-direction: column

  +lte_ipad
    gap: rem4(4px)

  +gte_laptop
    gap: 8px

.name-wrapper
  display: flex

  +lte_ipad
    align-items: center
    gap: 4px

  +gte_laptop
    flex-direction: column
    gap: 8px
    justify-content: center

  .name
    color: #000
    transition: color var(--transition-duration)

    +lte_ipad
      +p2(700)

    +gte_laptop
      +p4(700)

.discount,
.metro
  +lte_ipad
    +p5(500)

  +gte_laptop
    +p4(500)

.discount
  color: #08cc33

.metro
  align-items: center
  display: flex

  +lte_ipad
    gap: rem4(6px)

  +gte_laptop
    gap: 6px

  .line
    +lte_ipad
      border-radius: rem4(8px)
      height: rem4(8px)
      width: rem4(8px)

    +gte_laptop
      border-radius: 8px
      height: 8px
      width: 8px

.price-wrapper
  align-items: baseline
  display: flex

  +lte_ipad
    gap: rem4(8px)

  +gte_laptop
    gap: 16px

  .badge
    background: var(--gray-100)
    border-radius: 100px
    color: #000
    display: flex
    white-space: nowrap

    +lte_ipad
      +p5
      gap: rem4(4px)
      padding: rem4(4px) rem4(10px)

    +gte_laptop
      +p4
      gap: 4px
      padding: 4px 10px

    &.is-nearly-full:before,
    &.is-soon:before
      +lte_ipad
        font-size: rem4(15px)

      +gte_laptop
        font-size: 15px

    &.is-nearly-full
      color: #f79009
      background: #fffaeb

      &:before
        +font-icon('\E814')

    &.is-soon:before
      +font-icon('\E828')

  .price
    +p4(700)
    white-space: nowrap

.features,
.features li
  display: flex

  +lte_ipad
    gap: rem4(6px)

  +gte_laptop
    gap: 6px

.features
  color: var(--gray-500)

  +lte_ipad
    +p5

  +gte_laptop
    +p4

  li + li:before
    background: var(--gray-500)
    content: ''
    align-self: center

    +lte_ipad
      border-radius: rem4(2px)
      height: rem4(2px)
      width: rem4(2px)

    +gte_laptop
      border-radius: 2px
      height: 2px
      width: 2px

.photo
  background-position: center
  background-repeat: no-repeat
  background-size: cover

  +lte_ipad
    border-radius: rem4(12px)
    height: rem4(56px)
    width: rem4(56px)

  +gte_laptop
    border-radius: 24px
    height: 100px
    width: 100px

  &.is-missing
    background-image: url('@/../images/vue/storages_map-v4/storages_index/storage/no-image.svg')

  &.is-no-boxes
    filter: grayscale(100%)
    opacity: 0.3
</style>
